/**
 * 🔄 Hook para Suscripciones Realtime con Cleanup Automático
 * 
 * Proporciona suscripciones a Supabase Realtime con cleanup automático
 * para evitar memory leaks y conexiones acumuladas.
 * 
 * Características:
 * - Cleanup automático en unmount
 * - Cleanup cuando cambian dependencias
 * - Logging para debugging
 * - Manejo de errores
 * 
 * @example
 * ```tsx
 * useRealtimeSubscription({
 *   channel: 'orders',
 *   table: 'orders',
 *   filter: `tenant_id=eq.${tenantId}`,
 *   onInsert: (payload) => console.warn('Nueva orden:', payload),
 *   onUpdate: (payload) => console.warn('Orden actualizada:', payload),
 *   onDelete: (payload) => console.warn('Orden eliminada:', payload),
 *   enabled: !!tenantId,
 * });
 * ```
 */

import { useEffect, useRef } from 'react';
import { getSupabaseClient } from '../supabase';
import { RealtimeChannel } from '@supabase/supabase-js';
import { logDebug, logError, logWarn } from '../lib/logger';

export interface RealtimeSubscriptionOptions {
  /**
   * Nombre único del canal (debe ser único por suscripción)
   */
  channel: string;
  
  /**
   * Nombre de la tabla a suscribirse
   */
  table: string;
  
  /**
   * Filtro para la suscripción (formato PostgREST)
   * Ej: `tenant_id=eq.${tenantId}`
   */
  filter?: string;
  
  /**
   * Callback cuando se inserta un registro
   */
  onInsert?: (payload: any) => void;
  
  /**
   * Callback cuando se actualiza un registro
   */
  onUpdate?: (payload: any) => void;
  
  /**
   * Callback cuando se elimina un registro
   */
  onDelete?: (payload: any) => void;
  
  /**
   * Si la suscripción está habilitada (útil para condicionar)
   */
  enabled?: boolean;
  
  /**
   * Eventos adicionales personalizados
   */
  onEvent?: (event: string, payload: any) => void;
}

/**
 * Hook para suscripciones Realtime con cleanup automático
 */
export function useRealtimeSubscription(options: RealtimeSubscriptionOptions) {
  const {
    channel: channelName,
    table,
    filter,
    onInsert,
    onUpdate,
    onDelete,
    enabled = true,
    onEvent,
  } = options;

  // Ref para mantener referencia al canal (necesario para cleanup)
  const channelRef = useRef<RealtimeChannel | null>(null);
  const isSubscribedRef = useRef(false);

  useEffect(() => {
    // Si está deshabilitado, no suscribirse
    if (!enabled) {
      logDebug(`[Realtime] Suscripción '${channelName}' deshabilitada`);
      return;
    }

    // Si ya hay una suscripción activa, no crear otra
    if (isSubscribedRef.current) {
      logWarn(`[Realtime] Suscripción '${channelName}' ya está activa, saltando...`);
      return;
    }

    logDebug(`[Realtime] Iniciando suscripción '${channelName}' para tabla '${table}'`);

    const supabase = getSupabaseClient();
    try {
      const uniqueChannelName = `${channelName}-${Date.now()}`;
      const channel = supabase
        .channel(uniqueChannelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table,
            filter: filter || undefined,
          },
          (payload) => {
            const eventType = payload.eventType;
            logDebug(`[Realtime] Evento recibido en '${channelName}': ${eventType}`, payload);

            // Llamar callbacks según el tipo de evento
            switch (payload.eventType) {
              case 'INSERT':
                if (onInsert) {
                  try {
                    onInsert(payload);
                  } catch (error) {
                    logError(`[Realtime] Error en onInsert de '${channelName}':`, error);
                  }
                }
                break;

              case 'UPDATE':
                if (onUpdate) {
                  try {
                    onUpdate(payload);
                  } catch (error) {
                    logError(`[Realtime] Error en onUpdate de '${channelName}':`, error);
                  }
                }
                break;

              case 'DELETE':
                if (onDelete) {
                  try {
                    onDelete(payload);
                  } catch (error) {
                    logError(`[Realtime] Error en onDelete de '${channelName}':`, error);
                  }
                }
                break;

              default:
                if (onEvent) {
                  try {
                    onEvent(eventType, payload);
                  } catch (error) {
                    logError(`[Realtime] Error en onEvent de '${channelName}':`, error);
                  }
                }
            }
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            logDebug(`[Realtime] Suscripción '${channelName}' activa`);
            isSubscribedRef.current = true;
          } else if (status === 'CHANNEL_ERROR') {
            logError(`[Realtime] Error en canal '${channelName}'`);
          } else if (status === 'TIMED_OUT') {
            logWarn(`[Realtime] Timeout en canal '${channelName}'`);
          } else if (status === 'CLOSED') {
            logDebug(`[Realtime] Canal '${channelName}' cerrado`);
            isSubscribedRef.current = false;
          }
        });

      // Guardar referencia al canal para cleanup
      channelRef.current = channel;
      isSubscribedRef.current = true;

    } catch (error) {
      logError(`[Realtime] Error al crear suscripción '${channelName}':`, error);
    }

    // Cleanup: Remover canal cuando el componente se desmonte o cambien dependencias
    return () => {
      if (channelRef.current) {
        logDebug(`[Realtime] Limpiando suscripción '${channelName}'`);
        try {
          getSupabaseClient().removeChannel(channelRef.current);
          channelRef.current = null;
          isSubscribedRef.current = false;
        } catch (error) {
          logError(`[Realtime] Error al limpiar suscripción '${channelName}':`, error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, table, filter]); // Solo re-suscribirse si cambian estas dependencias críticas

  // Retornar función para suscripción manual si es necesario
  return {
    channel: channelRef.current,
    isSubscribed: isSubscribedRef.current,
  };
}

/**
 * Hook simplificado para suscripciones a una tabla específica
 * 
 * @example
 * ```tsx
 * useRealtimeTable('orders', {
 *   filter: `tenant_id=eq.${tenantId}`,
 *   onInsert: handleNewOrder,
 * });
 * ```
 */
export function useRealtimeTable(
  table: string,
  options: Omit<RealtimeSubscriptionOptions, 'channel' | 'table'> = {}
) {
  return useRealtimeSubscription({
    channel: `table-${table}`,
    table,
    ...options,
  });
}
